import React, { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router";
import type { User } from "firebase/auth";
import { authRedirectTarget, authUrlForProtectedRoute } from "./lib/authRedirect";

const LandingScreen = lazy(() => import("./LandingScreen"));
const AuthScreen = lazy(() => import("./AuthScreen"));
const HomeRoomScreen = lazy(() => import("./HomeRoomScreen"));
const WatchRoomScreen = lazy(() => import("./WatchRoomScreen"));
const LobbyScreen = lazy(() => import("./LobbyScreen"));
const TermsScreen = lazy(() => import("./TermsScreen"));
const PrivacyScreen = lazy(() => import("./PrivacyScreen"));
const OurSpaceScreen = lazy(() => import("./OurSpaceScreen"));
const AccountDataScreen = lazy(() => import("./AccountDataScreen"));
const PolicyConsentGate = lazy(() => import("./features/legal/PolicyConsentGate"));
const IntentLandingScreen = lazy(() => import("./IntentLandingScreen"));
const UnsubscribeScreen = lazy(() => import("./UnsubscribeScreen"));
const AdminMarketingScreen = lazy(() => import("./AdminMarketingScreen"));
const AdminSeoScreen = lazy(() => import("./AdminSeoScreen"));

function ScreenFallback() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f1a] to-[#1a1a2e] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 rounded-full border-4 border-pink-500 border-t-transparent animate-spin mx-auto mb-3" />
        <p className="text-sm text-white/50">Loading...</p>
      </div>
    </div>
  );
}

const RequireAuth = ({ children, user, loading }: { children: React.ReactNode; user: User | null; loading: boolean }) => {
  const location = useLocation();
  if (loading) return <ScreenFallback />;
  if (!user) return <Navigate to={authUrlForProtectedRoute(location.pathname, location.search)} replace />;
  return <PolicyConsentGate user={user}>{children}</PolicyConsentGate>;
};

const RequireGuest = ({ children, user, loading }: { children: React.ReactNode; user: User | null; loading: boolean }) => {
  const location = useLocation();
  if (loading) return <ScreenFallback />;
  if (user) return <Navigate to={authRedirectTarget(location.search)} replace />;
  return <>{children}</>;
};

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;

    void import("./lib/firebase-auth")
      .then(({ auth, onAuthStateChanged }) => {
        if (!active) return;
        unsubscribe = onAuthStateChanged(auth, (currentUser) => {
          setUser(currentUser);
          setLoading(false);
        });
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  return (
    <Suspense fallback={<ScreenFallback />}>
      <Routes>
        <Route path="/" element={!loading && user ? <Navigate to="/dashboard" replace /> : <LandingScreen />} />
        <Route path="/sign-up-login-screen" element={<RequireGuest user={user} loading={loading}><AuthScreen /></RequireGuest>} />
        <Route path="/terms" element={<TermsScreen />} />
        <Route path="/long-distance-date-night" element={<IntentLandingScreen intent="long-distance-date-night" />} />
        <Route path="/watch-youtube-together" element={<IntentLandingScreen intent="watch-youtube-together" />} />
        <Route path="/virtual-date-night" element={<IntentLandingScreen intent="virtual-date-night" />} />
        <Route path="/virtual-date-night-ideas" element={<IntentLandingScreen intent="virtual-date-night-ideas" />} />
        <Route path="/watch-movies-together" element={<IntentLandingScreen intent="watch-movies-together" />} />
        <Route path="/watch-party-for-couples" element={<IntentLandingScreen intent="watch-party-for-couples" />} />
        <Route path="/long-distance-relationship-app" element={<IntentLandingScreen intent="long-distance-relationship-app" />} />
        <Route path="/shared-watchlist-for-couples" element={<IntentLandingScreen intent="shared-watchlist-for-couples" />} />
        <Route path="/privacy" element={<PrivacyScreen />} />
        <Route path="/unsubscribe" element={<UnsubscribeScreen />} />
        <Route path="/dashboard" element={<RequireAuth user={user} loading={loading}><HomeRoomScreen /></RequireAuth>} />
        <Route path="/watch-room" element={<RequireAuth user={user} loading={loading}><WatchRoomScreen /></RequireAuth>} />
        <Route path="/lobby" element={<RequireAuth user={user} loading={loading}><LobbyScreen /></RequireAuth>} />
        <Route path="/our-space" element={<RequireAuth user={user} loading={loading}><OurSpaceScreen /></RequireAuth>} />
        <Route path="/account-data" element={<RequireAuth user={user} loading={loading}><AccountDataScreen /></RequireAuth>} />
        <Route path="/admin/marketing" element={<RequireAuth user={user} loading={loading}><AdminMarketingScreen /></RequireAuth>} />
        <Route path="/admin/seo" element={<RequireAuth user={user} loading={loading}><AdminSeoScreen /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
